import { Component, OnInit, Input } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CorporatePortfolioMovementService,
  CorporateEnrollmentWithdrawalInfo,
  DeferCorporateEnrollmentRequest,
  ContinueFromCorporateDeferralRequest,
  CorporatePortfolioMovementResult
} from '../../../services/corporate-portfolio-movement.service';
import { CompanyService } from '../../../services/company.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-corporate-deferral-wizard',
  templateUrl: './corporate-deferral-wizard.component.html',
  styleUrls: ['./corporate-deferral-wizard.component.scss']
})
export class CorporateDeferralWizardComponent implements OnInit {
  // 🔧 Exponer Math para uso en template
  Math = Math;

  @Input() wizardMode: 'new' | 'continue' = 'new';

  currentStep = 1;

  companies: any[] = [];
  selectedCompanyId: number | null = null;
  corporateEnrollments: CorporateEnrollmentWithdrawalInfo[] = [];
  deferredEnrollments: CorporateEnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: CorporateEnrollmentWithdrawalInfo | null = null;
  selectedInstallments: number[] = [];
  projects: any[] = [];

  searchForm: FormGroup;
  destinationForm: FormGroup;
  confirmForm: FormGroup;
  valueForm: FormGroup;

  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;

  operationResult: CorporatePortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';

  newPaymentPlan: { installmentNumber: number; dueDate: Date; amount: number; }[] = [];

  // Variables para Modal de Refinanciación
  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 24;

  constructor(
    private fb: FormBuilder,
    private corporatePortfolioService: CorporatePortfolioMovementService,
    private companyService: CompanyService,
    private projectService: ProjectService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({ searchTerm: ['', Validators.required] });
    this.destinationForm = this.fb.group({ 
      continueInSameProject: [true],
      newProjectId: [null] 
    });
    this.valueForm = this.fb.group({
      newTotalValueCOP: [0, [Validators.min(0)]],
      newTotalValueUSD: [0, [Validators.min(0)]],
      currencyId: [1]
    });
    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  // Métodos para Refinanciación Automática
  openRefinanceModal(): void {
    this.refinanceInstallments = Math.max(2, this.newPaymentPlan.length || 2);
    this.showRefinanceModal = true;
  }

  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  confirmRefinance(): void {
    if (this.refinanceInstallments < this.minInstallments || this.refinanceInstallments > this.maxInstallments) {
      this.toastService.showToast('error', `El número de cuotas debe estar entre ${this.minInstallments} y ${this.maxInstallments}.`);
      return;
    }
    this.generatePaymentPlan(this.refinanceInstallments);
    this.closeRefinanceModal();
  }

  // Obtener el valor activo según la moneda seleccionada
  getActiveTotal(): number {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    return currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
  }

  // Autollenar desde el proyecto seleccionado
  autofillFromProject(): void {
    const project = this.getSelectedProject();
    if (project) {
      this.valueForm.patchValue({
        newTotalValueCOP: project.inscriptionAmountCOP || project.inscriptionAmount || 0,
        newTotalValueUSD: project.inscriptionAmountUSD || 0
      });
    }
  }

  generatePaymentPlan(installmentsCount: number): void {
    const totalValue = this.getActiveTotal();
    if (totalValue <= 0) {
      this.toastService.showToast('warning', 'El valor total debe ser mayor a 0 para generar un plan.');
      return;
    }

    this.newPaymentPlan = [];
    const baseAmount = totalValue / installmentsCount;
    const roundedAmount = Math.round((baseAmount + Number.EPSILON) * 100) / 100;

    let accumulated = 0;

    for (let i = 0; i < installmentsCount; i++) {
      const isLast = i === installmentsCount - 1;
      let amount = roundedAmount;

      if (isLast) {
        amount = Math.round((totalValue - accumulated + Number.EPSILON) * 100) / 100;
      } else {
        accumulated += roundedAmount;
      }

      this.newPaymentPlan.push({
        installmentNumber: i + 1,
        dueDate: this.calculateNextDueDate(i + 1),
        amount: amount
      });
    }
    this.toastService.showToast('success', `Plan generado exitosamente en ${installmentsCount} cuotas.`);
  }

  private calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + installmentNumber);

    if (nextMonth.getMonth() !== ((today.getMonth() + installmentNumber) % 12)) {
      nextMonth.setDate(0);
    }

    return nextMonth;
  }

  getEstimatedInstallmentAmount(): number {
    const total = this.getActiveTotal();
    if (this.refinanceInstallments <= 0) return 0;
    return total / this.refinanceInstallments;
  }

  ngOnInit(): void {
    this.loadProjects();
    this.route.queryParams.subscribe(params => {
      if (params['corporateEnrollmentId']) {
        this.loadEnrollmentById(+params['corporateEnrollmentId']);
      }
      if (params['mode'] === 'continue') {
        this.wizardMode = 'continue';
      }
    });
  }

  get totalSteps(): number {
    if (this.wizardMode === 'new') {
      return 4; // Seleccionar -> Cuotas -> Confirmar -> Resultado
    }
    // Modo continue
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 4 : 6; // Mismo proyecto: 4 pasos, Nuevo proyecto: 6 pasos
  }

  get finalStepIndex(): number {
    return this.totalSteps;
  }

  get continueInSameProject(): boolean {
    return this.destinationForm.get('continueInSameProject')?.value === true;
  }

  isDestinationValid(): boolean {
    if (this.continueInSameProject) {
      return true; // No necesita proyecto si continúa en el mismo
    }
    return this.destinationForm.get('newProjectId')?.value != null;
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response) => {
        this.projects = this.extractData(response);
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar proyectos');
      }
    });
  }

  searchCompany(): void {
    if (this.searchForm.invalid) return;
    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;

    this.companyService.getAll().subscribe({
      next: (response) => {
        const allCompanies = this.extractData(response);
        this.companies = allCompanies.filter((c: any) =>
          c.name?.toLowerCase().includes(term.toLowerCase()) || c.documentId?.includes(term)
        );
        this.isSearching = false;
      },
      error: () => { this.toastService.showToast('error', 'Error al buscar'); this.isSearching = false; }
    });
  }

  selectCompany(company: any): void {
    this.selectedCompanyId = company.id;
    if (this.wizardMode === 'new') {
      this.loadActiveEnrollments(company.id);
    } else {
      this.loadDeferredEnrollments(company.id);
    }
  }

  loadActiveEnrollments(companyId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getActiveCorporateEnrollmentsByCompany(companyId).subscribe({
      next: (response) => {
        this.corporateEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.corporateEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Esta empresa no tiene acuerdos corporativos activos');
        }
      },
      error: () => { this.toastService.showToast('error', 'Error al cargar'); this.isLoading = false; }
    });
  }

  loadDeferredEnrollments(companyId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getDeferredCorporateEnrollmentsByCompany(companyId).subscribe({
      next: (response) => {
        this.deferredEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.deferredEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Esta empresa no tiene acuerdos corporativos aplazados');
        }
      },
      error: () => { this.toastService.showToast('error', 'Error al cargar'); this.isLoading = false; }
    });
  }

  loadEnrollmentById(id: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getCorporateEnrollmentWithdrawalInfo(id).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment) {
          if (enrollment.canBeDeferred) {
            this.wizardMode = 'new';
            this.selectedEnrollment = enrollment;
            this.selectAllInstallments();
          } else if (enrollment.canContinueFromDeferral) {
            this.wizardMode = 'continue';
            this.selectedEnrollment = enrollment;
          } else {
            this.toastService.showToast('error', 'Este acuerdo no puede ser aplazado o continuada');
            this.router.navigate(['/enrollments']);
            return;
          }
          this.currentStep = 2;
        }
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  selectEnrollment(enrollment: CorporateEnrollmentWithdrawalInfo): void {
    if (this.wizardMode === 'new' && !enrollment.canBeDeferred) {
      this.toastService.showToast('warning', 'Este acuerdo no tiene cuotas pendientes para aplazar');
      return;
    }
    if (this.wizardMode === 'continue' && !enrollment.canContinueFromDeferral) {
      this.toastService.showToast('warning', 'Este acuerdo no puede continuar');
      return;
    }
    this.selectedEnrollment = enrollment;
    if (this.wizardMode === 'new') {
      this.selectAllInstallments();
    } else {
      this.valueForm.patchValue({ newTotalValueCOP: enrollment.totalPendingAmount, newTotalValueUSD: 0, currencyId: 1 });
    }
    this.currentStep = 2;
  }

  toggleInstallment(id: number): void {
    const idx = this.selectedInstallments.indexOf(id);
    if (idx > -1) this.selectedInstallments.splice(idx, 1);
    else this.selectedInstallments.push(id);
  }

  selectAllInstallments(): void {
    if (!this.selectedEnrollment) return;
    this.selectedInstallments = [
      ...this.selectedEnrollment.companyPendingPlans.filter(p => !p.isPunished).map(p => p.corporatePaymentPlanId),
      ...this.selectedEnrollment.participantPendingPlans.filter(p => !p.isPunished).map(p => p.corporatePaymentPlanId)
    ];
  }

  deselectAllInstallments(): void { this.selectedInstallments = []; }
  isInstallmentSelected(id: number): boolean { return this.selectedInstallments.includes(id); }

  get selectedTotalAmount(): number {
    if (!this.selectedEnrollment) return 0;
    const company = this.selectedEnrollment.companyPendingPlans
      .filter(p => this.selectedInstallments.includes(p.corporatePaymentPlanId))
      .reduce((s, p) => s + p.pendingAmount, 0);
    const participant = this.selectedEnrollment.participantPendingPlans
      .filter(p => this.selectedInstallments.includes(p.corporatePaymentPlanId))
      .reduce((s, p) => s + p.pendingAmount, 0);
    return company + participant;
  }

  get allInstallmentsSelected(): boolean {
    if (!this.selectedEnrollment) return false;
    const total = this.selectedEnrollment.companyPendingPlans.filter(p => !p.isPunished).length +
      this.selectedEnrollment.participantPendingPlans.filter(p => !p.isPunished).length;
    return this.selectedInstallments.length === total && total > 0;
  }

  toggleSelectAll(): void {
    if (this.allInstallmentsSelected) this.deselectAllInstallments();
    else this.selectAllInstallments();
  }

  initializeNewPaymentPlan(): void {
    if (this.newPaymentPlan.length === 0) {
      const total = this.getActiveTotal();
      this.newPaymentPlan = [{
        installmentNumber: 1,
        dueDate: new Date(),
        amount: total
      }];
    }
  }

  addInstallment(): void {
    const nextNumber = this.newPaymentPlan.length + 1;
    const lastDate = this.newPaymentPlan.length > 0
      ? new Date(this.newPaymentPlan[this.newPaymentPlan.length - 1].dueDate)
      : new Date();

    if (this.newPaymentPlan.length > 0) {
      lastDate.setMonth(lastDate.getMonth() + 1);
    }

    this.newPaymentPlan.push({
      installmentNumber: nextNumber,
      dueDate: lastDate,
      amount: 0
    });
  }

  removeInstallment(index: number): void {
    this.newPaymentPlan.splice(index, 1);
    this.newPaymentPlan.forEach((p, i) => p.installmentNumber = i + 1);
  }

  get currentPlanTotal(): number {
    return this.newPaymentPlan.reduce((sum, p) => sum + (p.amount || 0), 0);
  }

  get isPlanValid(): boolean {
    const targetTotal = this.getActiveTotal();
    return Math.abs(this.currentPlanTotal - targetTotal) < 0.1 && this.newPaymentPlan.length > 0;
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      if (this.wizardMode === 'new' && this.selectedInstallments.length > 0) {
        this.currentStep = 3;
      } else if (this.wizardMode === 'continue' && this.isDestinationValid()) {
        if (this.continueInSameProject) {
          // Saltar a Confirmar directamente (paso 3 en flujo de 4 pasos)
          this.currentStep = 3;
        } else {
          // Ir a Modificar Valor (paso 3 en flujo de 6 pasos)
          this.currentStep = 3;
        }
      }
    } else if (this.currentStep === 3) {
      if (this.wizardMode === 'continue') {
        if (this.continueInSameProject) {
          // Ya está en Confirmar, no avanza más hasta submit
        } else if (this.valueForm.valid) {
          this.initializeNewPaymentPlan();
          this.currentStep = 4; // Ir a Plan de Pagos
        }
      }
    } else if (this.currentStep === 4) {
      if (this.wizardMode === 'continue' && !this.continueInSameProject && this.isPlanValid) {
        this.currentStep = 5; // Ir a Confirmar
      }
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) this.currentStep--;
  }

  setMode(mode: 'new' | 'continue'): void {
    this.wizardMode = mode;
    this.selectedEnrollment = null;
    this.corporateEnrollments = [];
    this.deferredEnrollments = [];
    this.selectedInstallments = [];
    if (this.selectedCompanyId) {
      if (mode === 'new') {
        this.loadActiveEnrollments(this.selectedCompanyId);
      } else {
        this.loadDeferredEnrollments(this.selectedCompanyId);
      }
    }
  }

  submitDeferral(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || this.isSubmitting) return;

    this.isSubmitting = true;

    if (this.wizardMode === 'new') {
      const request: DeferCorporateEnrollmentRequest = {
        corporateEnrollmentId: this.selectedEnrollment.corporateEnrollmentId,
        reason: this.confirmForm.get('reason')?.value,
        corporatePaymentPlanIds: this.selectedInstallments,
        notes: this.confirmForm.get('notes')?.value
      };

      this.corporatePortfolioService.deferCorporateEnrollment(request).subscribe({
        next: (response) => this.handleResponse(response),
        error: () => this.handleError()
      });
    } else {
      // Modo continue (desde aplazamiento)
      const continueInSame = this.continueInSameProject;

      let request: ContinueFromCorporateDeferralRequest;

      if (continueInSame) {
        // Reversión de aplazamiento - continuar en el mismo proyecto
        request = {
          corporateEnrollmentId: this.selectedEnrollment.corporateEnrollmentId,
          continueInSameProject: true,
          reason: this.confirmForm.get('reason')?.value,
          notes: this.confirmForm.get('notes')?.value
        };
      } else {
        // Continuar en nuevo proyecto - lógica original
        const cleanedPaymentPlan = this.newPaymentPlan.map(installment => ({
          installmentNumber: installment.installmentNumber,
          dueDate: installment.dueDate,
          amount: this.parseAmount(installment.amount)
        }));

        request = {
          corporateEnrollmentId: this.selectedEnrollment.corporateEnrollmentId,
          continueInSameProject: false,
          newProjectId: this.destinationForm.get('newProjectId')?.value,
          reason: this.confirmForm.get('reason')?.value,
          notes: this.confirmForm.get('notes')?.value,
          newTotalValue: this.getActiveTotal(),
          newPaymentPlan: cleanedPaymentPlan
        };
      }

      console.log('📤 Enviando solicitud de continuación corporativa:', request);

      this.corporatePortfolioService.continueFromCorporateDeferral(request).subscribe({
        next: (response) => this.handleResponse(response),
        error: () => this.handleError()
      });
    }
  }

  // Método para parsear montos que pueden venir como string formateado o número
  private parseAmount(value: any): number {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      // Remover formato de moneda: $ 1.234.567,89 -> 1234567.89
      const cleaned = value
        .replace(/[$\s]/g, '')  // Remover $ y espacios
        .replace(/\./g, '')     // Remover puntos de miles
        .replace(/,/g, '.');    // Convertir coma decimal a punto
      return parseFloat(cleaned) || 0;
    }
    return 0;
  }

  private handleResponse(response: any): void {
    const result = response.data || response;
    this.operationResult = result;
    this.isSubmitting = false;
    this.operationSuccess = result.success;
    this.operationError = result.success ? '' : (result.message || 'Error');
    this.currentStep = this.finalStepIndex;
  }

  private handleError(): void {
    this.operationSuccess = false;
    this.operationError = 'Error al procesar operación.';
    this.currentStep = this.finalStepIndex;
    this.isSubmitting = false;
  }

  cancel(): void { this.router.navigate(['/enrollments']); }
  goToEnrollmentsList(): void { this.router.navigate(['/enrollments']); }

  startNewDeferral(): void {
    this.currentStep = 1;
    this.wizardMode = 'new';
    this.selectedCompanyId = null;
    this.selectedEnrollment = null;
    this.selectedInstallments = [];
    this.corporateEnrollments = [];
    this.deferredEnrollments = [];
    this.companies = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.searchForm.reset();
    this.destinationForm.reset();
    this.confirmForm.reset();
    this.valueForm.reset({ newTotalValueCOP: 0, newTotalValueUSD: 0, currencyId: 1 });
    this.newPaymentPlan = [];
  }

  viewEnrollment(): void {
    const targetEnrollment = this.operationResult?.newCorporateEnrollmentId || this.selectedEnrollment?.corporateEnrollmentId;
    if (targetEnrollment) {
      this.router.navigate(['/enrollments/corporate/', targetEnrollment]);
    }
  }

  viewOriginalEnrollment(): void {
    if (this.selectedEnrollment) {
      this.router.navigate(['/enrollments/corporate/', this.selectedEnrollment.corporateEnrollmentId]);
    }
  }

  getSelectedProject(): any {
    const projectId = this.destinationForm.get('newProjectId')?.value;
    return this.projects.find(p => p.id === projectId);
  }

  formatCurrency(amount: number, currencySymbol?: string): string {
    const symbol = currencySymbol || '$';
    return `${symbol} ${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }
}
